import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, ArrowRight, Archive, FileSpreadsheet, FileDown, Repeat, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { exportClubPdf, exportClubXlsx } from "@/lib/export";

const KEEP = "__keep__";

export default function SeasonMigration() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dest, setDest] = useState({});
  const [applying, setApplying] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/coordinator/season-migration/preview")
      .then((r) => {
        setData(r.data);
        const d = {};
        (r.data.players || []).forEach((p) => {
          if (p.needs_move) d[p.player_id] = p.suggested_team_id || KEEP;
        });
        setDest(d);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const prevSeasonLabel = useMemo(() => {
    const y = (data?.season_start_year || new Date().getFullYear()) - 1;
    return `${y}/${String(y + 1).slice(-2)}`;
  }, [data]);
  const prevFrom = `${(data?.season_start_year || 2026) - 1}-07-01`;
  const prevTo = `${data?.season_start_year || 2026}-06-30`;

  const toMove = useMemo(
    () => (data?.players || []).filter((p) => p.needs_move),
    [data],
  );
  const okPlayers = useMemo(
    () => (data?.players || []).filter((p) => !p.needs_move),
    [data],
  );
  const pendingMoves = useMemo(
    () => toMove.filter((p) => dest[p.player_id] && dest[p.player_id] !== KEEP
      && dest[p.player_id] !== p.current_team_id),
    [toMove, dest],
  );

  const apply = async () => {
    if (pendingMoves.length === 0) {
      toast.error("No hay movimientos seleccionados");
      return;
    }
    if (!window.confirm(`¿Mover ${pendingMoves.length} jugador(es) a su nuevo equipo para la temporada ${data?.season_label}?`)) return;
    setApplying(true);
    try {
      const { data: res } = await api.post("/coordinator/season-migration/apply", {
        moves: pendingMoves.map((p) => ({ player_id: p.player_id, team_id: dest[p.player_id] })),
      });
      toast.success(`${res.moved} jugador(es) migrados a la temporada ${data?.season_label}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="season-migration-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/coordinador")} data-testid="migration-back">
        <ArrowLeft className="h-4 w-4 mr-2" /> Volver al panel
      </Button>

      <div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight flex items-center gap-2"
            style={{ fontFamily: "Nunito" }}>
          <Repeat className="h-7 w-7 text-purple-600" /> Migración de temporada {data?.season_label || ""}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Archiva la temporada {prevSeasonLabel} y promociona a los jugadores a su categoría según su año de nacimiento.
        </p>
      </div>

      {/* Paso 1 — Archivar */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Archive className="h-4 w-4 text-purple-600" />
            Paso 1 · Archivar temporada {prevSeasonLabel}
          </CardTitle>
          <p className="text-xs text-slate-500">
            Descarga una copia completa de la temporada pasada antes de migrar. Los datos no se borran:
            siempre podrás consultarlos filtrando por fechas.
          </p>
        </CardHeader>
        <CardContent className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            onClick={() => exportClubPdf({ date_from: prevFrom, date_to: prevTo })}
            data-testid="archive-club-pdf"
          >
            <FileDown className="h-4 w-4 mr-2" /> Club PDF {prevSeasonLabel}
          </Button>
          <Button
            variant="outline"
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            onClick={() => exportClubXlsx({ date_from: prevFrom, date_to: prevTo })}
            data-testid="archive-club-xlsx"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Club Excel {prevSeasonLabel}
          </Button>
        </CardContent>
      </Card>

      {/* Paso 2 — Promociones */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-purple-600" />
            Paso 2 · Promocionar jugadores ({toMove.length} pendientes)
          </CardTitle>
          <p className="text-xs text-slate-500">
            Jugadores cuya categoría cambia en la temporada {data?.season_label}. Revisa el equipo de
            destino propuesto y pulsa &quot;Aplicar migración&quot;.
            {data?.no_birth_year_count > 0 && (
              <span className="text-amber-600 font-semibold">
                {" "}({data.no_birth_year_count} jugador(es) sin año de nacimiento — revísalos en Jugadores)
              </span>
            )}
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-slate-400 italic py-6 text-center">Cargando…</div>
          ) : toMove.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center flex items-center justify-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              Todos los jugadores están en la categoría correcta para la {data?.season_label}.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Jugador</TableHead>
                      <TableHead className="text-center">Año</TableHead>
                      <TableHead>Equipo actual</TableHead>
                      <TableHead className="text-center">Categoría {data?.season_label}</TableHead>
                      <TableHead>Equipo destino</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {toMove.map((p) => (
                      <TableRow key={p.player_id} data-testid={`migration-row-${p.player_id}`}>
                        <TableCell className="font-semibold whitespace-nowrap">
                          {p.dorsal ? `#${p.dorsal} ` : ""}{p.player_name}
                        </TableCell>
                        <TableCell className="text-center tabular-nums">{p.birth_year}</TableCell>
                        <TableCell className="text-sm">
                          {p.current_team_name}
                          <span className="text-xs text-slate-400"> · {p.current_category || "—"}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-800">
                            {p.suggested_category}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={dest[p.player_id] || KEEP}
                            onValueChange={(v) => setDest((d) => ({ ...d, [p.player_id]: v }))}
                          >
                            <SelectTrigger className="w-56" data-testid={`migration-dest-${p.player_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={KEEP}>Mantener en {p.current_team_name}</SelectItem>
                              {p.candidate_teams.map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.name} ({t.category})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-end mt-4">
                <Button
                  onClick={apply}
                  disabled={applying || pendingMoves.length === 0}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                  data-testid="migration-apply"
                >
                  {applying ? "Aplicando…" : `Aplicar migración (${pendingMoves.length})`}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Resumen de correctos */}
      {!loading && okPlayers.length > 0 && (
        <p className="text-xs text-slate-500" data-testid="migration-ok-count">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 inline mr-1" />
          {okPlayers.length} jugador(es) ya están en un equipo compatible con su categoría {data?.season_label}.
        </p>
      )}
    </div>
  );
}
